import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress, Grid, Chip, IconButton,
  Tabs, Tab, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import BarChartIcon from '@mui/icons-material/BarChart';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const API = '/api/contabilidad';
const MESES = ['01','02','03','04','05','06','07','08','09','10','11','12'];
const MESES_NOM = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
function fmt(v) { return parseFloat(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtShort(v) {
  const n = parseFloat(v || 0);
  if (Math.abs(n) >= 1000) return (n/1000).toFixed(1) + 'k';
  return n.toFixed(0);
}

const FORM_INIT = { cuenta_id: '', centro_costo_id: '', descripcion: '' };
const MESES_INIT = Object.fromEntries(MESES.map(m => [m, '']));

export default function Presupuesto() {
  const [tab, setTab] = useState(0);
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [anio, setAnio] = useState(new Date().getFullYear().toString());
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [lineas, setLineas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_INIT);
  const [meses, setMeses] = useState(MESES_INIT);
  const [saving, setSaving] = useState(false);

  // Reporte
  const [mesDesde, setMesDesde] = useState('1');
  const [mesHasta, setMesHasta] = useState('12');
  const [reporte, setReporte] = useState(null);
  const [loadingRep, setLoadingRep] = useState(false);

  const anios = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 1 + i);

  useEffect(() => { axios.get(`${API}/empresas`).then(r => setEmpresas(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!empresaId) return;
    axios.get(`${API}/plan?empresa_id=${empresaId}`).then(r => setCuentas(r.data.filter(c => c.nivel >= 2))).catch(() => {});
    axios.get(`${API}/centros-costo?empresa_id=${empresaId}`).then(r => setCentrosCosto(r.data.filter(c => c.activo))).catch(() => {});
  }, [empresaId]);

  useEffect(() => { if (empresaId && anio) cargarLineas(); }, [empresaId, anio]);

  const cargarLineas = useCallback(async () => {
    if (!empresaId || !anio) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/presupuesto?empresa_id=${empresaId}&anio=${anio}`);
      setLineas(r.data);
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
    setLoading(false);
  }, [empresaId, anio]);

  function abrirNueva() { setEditando(null); setForm(FORM_INIT); setMeses(MESES_INIT); setDlgOpen(true); }
  function abrirEditar(l) {
    setEditando(l);
    setForm({ cuenta_id: l.cuenta_id, centro_costo_id: l.centro_costo_id || '', descripcion: l.descripcion || '' });
    setMeses(Object.fromEntries(MESES.map(m => [m, l[`mes_${m}`] !== undefined ? String(l[`mes_${m}`]) : ''])));
    setDlgOpen(true);
  }

  async function guardar() {
    if (!form.cuenta_id) return setError('Selecciona una cuenta');
    setSaving(true);
    try {
      await axios.post(`${API}/presupuesto`, {
        empresa_id: empresaId, anio, cuenta_id: form.cuenta_id,
        centro_costo_id: form.centro_costo_id || null,
        descripcion: form.descripcion,
        meses: Object.fromEntries(MESES.map(m => [m, parseFloat(meses[m]) || 0]))
      });
      setSuccess('Línea guardada'); setDlgOpen(false); cargarLineas();
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
    setSaving(false);
  }

  async function eliminar(l) {
    if (!window.confirm('¿Eliminar esta línea de presupuesto?')) return;
    try { await axios.delete(`${API}/presupuesto/${l.id}`); setSuccess('Eliminado'); cargarLineas(); }
    catch (e) { setError(e.response?.data?.error || 'Error'); }
  }

  async function generarReporte() {
    if (!empresaId || !anio) return setError('Selecciona empresa y año');
    setLoadingRep(true); setError('');
    try {
      const r = await axios.get(`${API}/presupuesto/reporte?empresa_id=${empresaId}&anio=${anio}&mes_desde=${mesDesde}&mes_hasta=${mesHasta}`);
      setReporte(r.data);
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
    setLoadingRep(false);
  }

  const mesesFiltro = reporte?.meses || [];

  function varColor(v) { if (v > 0) return 'success.main'; if (v < 0) return 'error.main'; return 'text.secondary'; }

  return (
    <Box sx={{ width: '100%', maxWidth: 1600, mx: 'auto', mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <TrendingUpIcon color="primary" />
        <Typography variant="h5" fontWeight="bold">Presupuesto vs. Real</Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Cargar Presupuesto" />
        <Tab label="Reporte Comparativo" icon={<BarChartIcon />} iconPosition="start" />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Selectores comunes */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm="auto">
            <TextField select fullWidth size="small" label="Empresa" value={empresaId} onChange={e => setEmpresaId(e.target.value)} sx={{ minWidth: '24em' }}>
              {empresas.map(e => <MenuItem key={e.id} value={e.id}>{e.razon_social}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm="auto">
            <TextField select fullWidth size="small" label="Año" value={anio} onChange={e => { setAnio(e.target.value); setReporte(null); }} sx={{ minWidth: '20em' }}>
              {anios.map(a => <MenuItem key={a} value={String(a)}>{a}</MenuItem>)}
            </TextField>
          </Grid>
          {tab === 0 && (
            <Grid item xs={12} sm="auto">
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNueva} disabled={!empresaId}>Nueva Línea</Button>
                <IconButton onClick={cargarLineas} disabled={!empresaId || loading}><RefreshIcon /></IconButton>
              </Box>
            </Grid>
          )}
          {tab === 1 && (
            <>
              <Grid item xs={12} sm="auto">
                <TextField select fullWidth size="small" label="Mes desde" value={mesDesde} onChange={e => setMesDesde(e.target.value)} sx={{ minWidth: '20em' }}>
                  {MESES.map((m, i) => <MenuItem key={m} value={String(i+1)}>{MESES_NOM[i]}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm="auto">
                <TextField select fullWidth size="small" label="Mes hasta" value={mesHasta} onChange={e => setMesHasta(e.target.value)} sx={{ minWidth: '20em' }}>
                  {MESES.map((m, i) => <MenuItem key={m} value={String(i+1)}>{MESES_NOM[i]}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm="auto">
                <Button variant="contained" startIcon={<BarChartIcon />} onClick={generarReporte} disabled={!empresaId || loadingRep}>
                  {loadingRep ? <CircularProgress size={18} color="inherit" /> : 'Generar'}
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* ── TAB 0: CARGA ── */}
      {tab === 0 && (
        loading ? <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} /> : (
          <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Cuenta</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Centro Costo</TableCell>
                  {MESES_NOM.map(m => <TableCell key={m} sx={{ color: 'white', fontWeight: 'bold' }} align="right">{m}</TableCell>)}
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Total</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Acc.</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineas.length === 0 ? (
                  <TableRow><TableCell colSpan={16} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay líneas de presupuesto para {anio}</TableCell></TableRow>
                ) : lineas.map(l => {
                  const total = MESES.reduce((s, m) => s + parseFloat(l[`mes_${m}`] || 0), 0);
                  return (
                    <TableRow key={l.id} hover>
                      <TableCell sx={{ minWidth: 180 }}>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{l.cuenta_codigo}</Typography>
                        <Typography variant="body2">{l.cuenta_nombre}</Typography>
                      </TableCell>
                      <TableCell>{l.cc_codigo ? `${l.cc_codigo} ${l.cc_nombre}` : '—'}</TableCell>
                      {MESES.map(m => (
                        <TableCell key={m} align="right" sx={{ fontSize: '0.75rem' }}>
                          {parseFloat(l[`mes_${m}`]) > 0 ? fmtShort(l[`mes_${m}`]) : '—'}
                        </TableCell>
                      ))}
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>S/ {fmt(total)}</TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Editar"><IconButton size="small" onClick={() => abrirEditar(l)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => eliminar(l)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      {/* ── TAB 1: REPORTE ── */}
      {tab === 1 && reporte && (
        <Box>
          {reporte.lineas.length === 0 ? (
            <Alert severity="info">No hay líneas de presupuesto para {anio}. Carga el presupuesto primero.</Alert>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.800' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 180, position: 'sticky', left: 0, bgcolor: 'grey.800', zIndex: 1 }}>Cuenta</TableCell>
                    {mesesFiltro.map((m, i) => (
                      <TableCell key={m} colSpan={3} align="center" sx={{ color: 'white', fontWeight: 'bold', borderLeft: '1px solid rgba(255,255,255,0.2)' }}>
                        {MESES_NOM[parseInt(m)-1]}
                      </TableCell>
                    ))}
                    <TableCell colSpan={3} align="center" sx={{ color: 'white', fontWeight: 'bold', borderLeft: '1px solid rgba(255,255,255,0.2)' }}>TOTAL</TableCell>
                    <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>%</TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: 'grey.600' }}>
                    <TableCell sx={{ color: 'white', fontSize: '0.7rem', position: 'sticky', left: 0, bgcolor: 'grey.600', zIndex: 1 }}>—</TableCell>
                    {mesesFiltro.map(m => (
                      <React.Fragment key={m}>
                        <TableCell align="right" sx={{ color: '#aed6f1', fontSize: '0.7rem' }}>Ppto.</TableCell>
                        <TableCell align="right" sx={{ color: '#a9dfbf', fontSize: '0.7rem' }}>Real</TableCell>
                        <TableCell align="right" sx={{ color: '#f9e79f', fontSize: '0.7rem' }}>Var.</TableCell>
                      </React.Fragment>
                    ))}
                    <TableCell align="right" sx={{ color: '#aed6f1', fontSize: '0.7rem' }}>Ppto.</TableCell>
                    <TableCell align="right" sx={{ color: '#a9dfbf', fontSize: '0.7rem' }}>Real</TableCell>
                    <TableCell align="right" sx={{ color: '#f9e79f', fontSize: '0.7rem' }}>Var.</TableCell>
                    <TableCell align="right" sx={{ color: '#f9e79f', fontSize: '0.7rem' }}>Ejec.</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reporte.lineas.map(l => (
                    <TableRow key={l.id} hover>
                      <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>{l.cuenta_codigo}</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{l.cuenta_nombre}</Typography>
                      </TableCell>
                      {mesesFiltro.map(m => (
                        <React.Fragment key={m}>
                          <TableCell align="right" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{l[`ppto_${m}`] ? fmtShort(l[`ppto_${m}`]) : '—'}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{l[`real_${m}`] ? fmtShort(l[`real_${m}`]) : '—'}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem', color: varColor(l[`var_${m}`]), fontWeight: l[`var_${m}`] ? 'bold' : 'normal' }}>
                            {l[`var_${m}`] ? (l[`var_${m}`] > 0 ? '+' : '') + fmtShort(l[`var_${m}`]) : '—'}
                          </TableCell>
                        </React.Fragment>
                      ))}
                      <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem', color: 'text.secondary' }}>S/ {fmt(l.total_ppto)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>S/ {fmt(l.total_real)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem', color: varColor(l.total_var) }}>
                        {l.total_var > 0 ? '+' : ''}S/ {fmt(l.total_var)}
                      </TableCell>
                      <TableCell align="right">
                        {l.pct_ejecucion !== null ? (
                          <Chip label={`${l.pct_ejecucion}%`} size="small"
                            color={l.pct_ejecucion >= 100 ? 'success' : l.pct_ejecucion >= 75 ? 'warning' : 'error'}
                          />
                        ) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Dialog nueva/editar línea */}
      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editando ? 'Editar Línea de Presupuesto' : 'Nueva Línea de Presupuesto'} — {anio}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Cuenta *" value={form.cuenta_id} onChange={e => setForm(f => ({ ...f, cuenta_id: e.target.value }))} disabled={!!editando}>
                {cuentas.map(c => <MenuItem key={c.id} value={c.id}>{c.codigo} — {c.descripcion}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Centro de Costo (opcional)" value={form.centro_costo_id} onChange={e => setForm(f => ({ ...f, centro_costo_id: e.target.value }))}>
                <MenuItem value="">Sin centro de costo</MenuItem>
                {centrosCosto.map(c => <MenuItem key={c.id} value={c.id}>{c.codigo} — {c.descripcion}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Descripción (opcional)" value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} />
            </Grid>
            <Grid item xs={12}><Typography variant="subtitle2" color="text.secondary">Montos mensuales (S/)</Typography></Grid>
            {MESES.map((m, i) => (
              <Grid key={m} item xs={3} sm={2}>
                <TextField fullWidth size="small" label={MESES_NOM[i]} type="number" value={meses[m]}
                  onChange={e => setMeses(prev => ({ ...prev, [m]: e.target.value }))}
                  inputProps={{ step: '0.01', min: '0' }} />
              </Grid>
            ))}
            <Grid item xs={12}>
              <Typography variant="body2" color="primary" fontWeight="bold">
                Total año: S/ {fmt(MESES.reduce((s, m) => s + (parseFloat(meses[m]) || 0), 0))}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDlgOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={guardar} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

